<template>
  <div
    v-if="showModal"
    class="modal d-block"
    style="background: rgba(0, 0, 0, 0.5)"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Share Your Health Insights</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Security Warning -->
          <div v-if="securityWarning" class="alert alert-warning d-flex align-items-center mb-3">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <span v-html="escapeForDisplay(securityWarning)"></span>
          </div>
          
          <!-- Content Analysis -->
          <div v-if="contentAnalysis && contentAnalysis.riskLevel === 'high'" class="alert alert-danger d-flex align-items-center mb-3">
            <i class="bi bi-shield-exclamation me-2"></i>
            <div>
              <strong>Content Security Alert:</strong> {{ contentAnalysis.recommendation }}
              <ul class="mb-0 mt-1">
                <li v-for="threat in contentAnalysis.threats" :key="threat">{{ threat }}</li>
              </ul>
            </div>
          </div>
          
          <form @submit.prevent="submitPost">
            <div class="mb-3">
              <label class="form-label">Select Topic <span class="text-danger">*</span></label>
              <select 
                class="form-select" 
                :class="{ 'is-invalid': errors.topic }"
                v-model="postForm.topic" 
                @change="validateTopic"
                required
              >
                <option value="">Please select a topic</option>
                <option v-for="topic in topics" :key="topic.id" :value="topic.id">
                  {{ escapeForDisplay(topic.name) }}
                </option>
              </select>
              <div v-if="errors.topic" class="invalid-feedback">{{ errors.topic }}</div>
            </div>
            
            <div class="mb-3">
              <label class="form-label">Title (Optional)</label>
              <input 
                type="text" 
                class="form-control" 
                :class="{ 'is-invalid': errors.title }"
                v-model="postForm.title" 
                @blur="validateTitle"
                @input="clearError('title')"
                placeholder="Give your post a title"
                maxlength="200"
              >
              <div v-if="errors.title" class="invalid-feedback">{{ errors.title }}</div>
              <small class="text-muted">{{ postForm.title.length }}/200 characters</small>
            </div>
            
            <div class="mb-3">
              <label class="form-label">Content <span class="text-danger">*</span></label>
              <textarea 
                class="form-control" 
                :class="{ 'is-invalid': errors.content }"
                rows="6" 
                v-model="postForm.content" 
                @blur="validateContent"
                @input="clearError('content')"
                placeholder="Share your health insights, experiences or thoughts..."
                maxlength="2000"
                required
              ></textarea>
              <div v-if="errors.content" class="invalid-feedback">{{ errors.content }}</div>
              <small class="text-muted">{{ postForm.content.length }}/2000 characters</small>
            </div>
            
            <div class="mb-3">
              <label class="form-label">Tags</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="tagInput"
                @keyup.enter="addTag"
                placeholder="Enter tags and press Enter to add (max 5 tags)"
                maxlength="50"
              >
              <small class="text-muted">{{ postForm.tags.length }}/5 tags</small>
              <div v-if="postForm.tags.length > 0" class="tags-display mt-2">
                <span 
                  v-for="tag in postForm.tags" 
                  :key="tag"
                  class="badge bg-secondary me-1"
                >
                  {{ escapeForDisplay(tag) }}
                  <i class="bi bi-x ms-1" @click="removeTag(tag)" style="cursor: pointer;"></i>
                </span>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="submitPost"
            :disabled="!isFormValid || isSubmitting || (contentAnalysis && contentAnalysis.riskLevel === 'high')"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ isSubmitting ? 'Publishing...' : 'Publish' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useValidation } from '@/composables/useValidation'
import { useSecurity } from '@/composables/useSecurity'

export default {
  name: 'CreatePostModal',
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    topics: {
      type: Array,
      required: true
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { validateAndSanitizeText, validateRequired } = useValidation()
    const { sanitizeFormData, analyzeContentSecurity, escapeForDisplay } = useSecurity()
    
    return {
      validateAndSanitizeText,
      validateRequired,
      sanitizeFormData,
      analyzeContentSecurity,
      escapeForDisplay
    }
  },
  emits: ['close', 'submit'],
  data() {
    return {
      postForm: {
        topic: '',
        title: '',
        content: '',
        tags: []
      },
      tagInput: '',
      errors: {},
      securityWarning: '',
      contentAnalysis: null
    }
  },
  computed: {
    isFormValid() {
      return this.postForm.content.trim() && 
             this.postForm.topic && 
             !this.errors.content && 
             !this.errors.title && 
             !this.errors.topic &&
             !(this.contentAnalysis && this.contentAnalysis.riskLevel === 'high')
    }
  },
  methods: {
    validateTopic() {
      if (!this.postForm.topic) {
        this.errors.topic = 'Please select a topic'
      } else {
        delete this.errors.topic
      }
      this.$forceUpdate()
    },
    
    validateTitle() {
      if (this.postForm.title) {
        const result = this.validateAndSanitizeText(this.postForm.title, 'title', {
          required: false,
          maxLength: 200,
          allowHtml: false
        })
        
        if (!result.isValid) {
          // Error already set by validator
        } else {
          this.postForm.title = result.sanitizedValue
          delete this.errors.title
        }
      } else {
        delete this.errors.title
      }
      this.$forceUpdate()
    },
    
    validateContent() {
      const result = this.validateAndSanitizeText(this.postForm.content, 'content', {
        required: true,
        minLength: 10,
        maxLength: 2000,
        allowHtml: false
      })
      
      if (!result.isValid) {
        // Error already set by validator
      } else {
        this.postForm.content = result.sanitizedValue
        
        // Perform security analysis on content
        this.contentAnalysis = this.analyzeContentSecurity(this.postForm.content)
        
        if (this.contentAnalysis.riskLevel === 'high') {
          this.securityWarning = 'Your content contains potentially unsafe elements. Please review and remove any suspicious content before publishing.'
        } else if (this.contentAnalysis.riskLevel === 'medium') {
          this.securityWarning = 'Your content may contain elements that could be flagged. Please review before publishing.'
        } else {
          this.securityWarning = ''
        }
        
        delete this.errors.content
      }
      this.$forceUpdate()
    },
    
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field]
        this.$forceUpdate()
      }
    },
    
    submitPost() {
      // Clear warnings
      this.securityWarning = ''
      
      // Validate all fields
      this.validateTopic()
      this.validateTitle()
      this.validateContent()
      
      if (!this.isFormValid) {
        this.securityWarning = 'Please fix all validation errors before submitting.'
        return
      }
      
      // Final sanitization of all form data
      const sanitizedData = this.sanitizeFormData({
        topic: this.postForm.topic,
        title: this.postForm.title,
        content: this.postForm.content,
        tags: this.postForm.tags.map(tag => this.escapeForDisplay(tag))
      })
      
      // Emit sanitized data
      this.$emit('submit', sanitizedData)
    },
    
    addTag() {
      if (this.postForm.tags.length >= 5) {
        this.securityWarning = 'Maximum 5 tags allowed.'
        return
      }
      
      const tag = this.tagInput.trim()
      if (!tag) return
      
      // Sanitize tag
      const result = this.validateAndSanitizeText(tag, 'tag', {
        required: true,
        maxLength: 50,
        allowHtml: false
      })
      
      if (!result.isValid) {
        this.securityWarning = 'Tag contains invalid characters or is too long.'
        return
      }
      
      const sanitizedTag = result.sanitizedValue
      
      if (!this.postForm.tags.includes(sanitizedTag)) {
        this.postForm.tags.push(sanitizedTag)
        this.securityWarning = ''
      }
      
      this.tagInput = ''
    },
    
    removeTag(tag) {
      this.postForm.tags = this.postForm.tags.filter(t => t !== tag)
    },
    
    resetForm() {
      this.postForm = {
        topic: '',
        title: '',
        content: '',
        tags: []
      }
      this.tagInput = ''
      this.errors = {}
      this.securityWarning = ''
      this.contentAnalysis = null
    }
  },
  watch: {
    showModal(newVal) {
      if (!newVal) {
        this.resetForm()
      }
    }
  }
}
</script>

<style scoped>
.tags-display .badge {
  font-size: 0.8rem;
  padding: 0.5rem;
}

.tags-display .badge i {
  font-size: 0.7rem;
}

.modal-content {
  border-radius: 15px;
}

.modal-header {
  border-bottom: 1px solid #eee;
  padding: 1.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #eee;
  padding: 1.5rem;
}

.form-label {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-control, .form-select {
  border-radius: 10px;
  border: 1px solid #ddd;
  padding: 0.75rem;
  transition: all 0.3s ease;
}

.form-control:focus, .form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

textarea.form-control {
  resize: vertical;
  min-height: 150px;
}

.btn {
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}
</style>